import { devLog } from './devLog';
/**
 * Commit Message Storage Manager
 * 
 * Manages pending commit messages for file modifications per tenant, project, and service.
 * Messages are stored in IndexedDB and cleared upon git operations (push, pull, reset).
 * 
 * Features:
 * - Append-mode message storage per tenant/project/service
 * - Automatic message clearing on push/pull/reset operations
 * - Message retrieval for commit generation
 * - Thread-safe IndexedDB operations
 */

const DB_NAME = 'CommitMessageStorage';
const DB_VERSION = 1;
const MESSAGES_STORE = 'commitMessages';

class CommitMessageStorage {
  constructor() {
    this.db = null;
    this.initPromise = null;
  }

  /**
   * Initialize the IndexedDB database
   */
  async init() {
    if (this.db) return this.db;
    if (this.initPromise) return this.initPromise;

    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('Failed to open CommitMessageStorage database:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // Create commit messages store
        if (!db.objectStoreNames.contains(MESSAGES_STORE)) {
          const messagesStore = db.createObjectStore(MESSAGES_STORE, { keyPath: 'id' });
          
          // Create indexes for efficient querying
          messagesStore.createIndex('tenantId', 'tenantId', { unique: false });
          messagesStore.createIndex('projectId', 'projectId', { unique: false });
          messagesStore.createIndex('serviceName', 'serviceName', { unique: false });
          messagesStore.createIndex('tenantProjectService', ['tenantId', 'projectId', 'serviceName'], { unique: true });
          messagesStore.createIndex('timestamp', 'timestamp', { unique: false });
        }
      };
    });

    return this.initPromise;
  }

  /**
   * Generate a unique ID for tenant/project/service combination
   */
  generateId(tenantId, projectId, serviceName) {
    const tenant = tenantId || 'default-tenant';
    const project = projectId || 'default-project';
    const service = serviceName || 'default-service';
    return `${tenant}__${project}__${service}`;
  }

  /**
   * Append a message to the commit message for a specific tenant/project/service
   * @param {string} tenantId - Tenant ID
   * @param {string} projectId - Project ID
   * @param {string} serviceName - Service name
   * @param {string} message - Message to append
   */
  async appendMessage(tenantId, projectId, serviceName, message) {
    try {
      await this.init();
      
      const id = this.generateId(tenantId, projectId, serviceName);
      
      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([MESSAGES_STORE], 'readwrite');
        const store = transaction.objectStore(MESSAGES_STORE);
        
        // First, try to get existing message
        const getRequest = store.get(id);
        
        getRequest.onsuccess = () => {
          const existingRecord = getRequest.result;
          
          let newMessages = [];
          if (existingRecord && existingRecord.messages) {
            newMessages = [...existingRecord.messages, message];
          } else {
            newMessages = [message];
          }
          
          const record = {
            id,
            tenantId: tenantId || null,
            projectId: projectId || null,
            serviceName: serviceName || null,
            messages: newMessages,
            timestamp: Date.now(),
            lastModified: Date.now()
          };
          
          const putRequest = store.put(record);
          
          putRequest.onsuccess = () => {
            resolve(record);
          };
          
          putRequest.onerror = () => {
            console.error('Failed to append commit message:', putRequest.error);
            reject(putRequest.error);
          };
        };
        
        getRequest.onerror = () => {
          console.error('Failed to get existing commit message:', getRequest.error);
          reject(getRequest.error);
        };
      });
    } catch (error) {
      console.error('Failed to append commit message:', error);
      throw error;
    }
  }

  /**
   * Get the commit message for a specific tenant/project/service
   * @param {string} tenantId - Tenant ID
   * @param {string} projectId - Project ID
   * @param {string} serviceName - Service name
   * @returns {Promise<string|null>} - Combined commit message or null if none exists
   */
  async getMessage(tenantId, projectId, serviceName) {
    try {
      await this.init();
      
      const id = this.generateId(tenantId, projectId, serviceName);
      
      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([MESSAGES_STORE], 'readonly');
        const store = transaction.objectStore(MESSAGES_STORE);
        const request = store.get(id);
        
        request.onsuccess = () => {
          const record = request.result;
          if (record && record.messages && record.messages.length > 0) {
            // Join messages with newlines
            const combinedMessage = record.messages.join('\n');
            resolve(combinedMessage);
          } else {
            resolve(null);
          }
        };
        
        request.onerror = () => {
          console.error('Failed to get commit message:', request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('Failed to get commit message:', error);
      throw error;
    }
  }

  /**
   * Get all individual messages for a specific tenant/project/service
   * @param {string} tenantId - Tenant ID
   * @param {string} projectId - Project ID
   * @param {string} serviceName - Service name
   * @returns {Promise<string[]>} - Array of messages
   */
  async getMessages(tenantId, projectId, serviceName) {
    try {
      await this.init();
      
      const id = this.generateId(tenantId, projectId, serviceName);
      
      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([MESSAGES_STORE], 'readonly');
        const store = transaction.objectStore(MESSAGES_STORE);
        const request = store.get(id);
        
        request.onsuccess = () => {
          const record = request.result;
          if (record && record.messages) {
            resolve(record.messages);
          } else {
            resolve([]);
          }
        };
        
        request.onerror = () => {
          console.error('Failed to get commit messages:', request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('Failed to get commit messages:', error);
      throw error;
    }
  }

  /**
   * Clear the commit message for a specific tenant/project/service
   * This is called after push, pull, or reset operations
   * @param {string} tenantId - Tenant ID
   * @param {string} projectId - Project ID
   * @param {string} serviceName - Service name
   */
  async clearMessage(tenantId, projectId, serviceName) {
    try {
      await this.init();
      
      const id = this.generateId(tenantId, projectId, serviceName);
      
      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([MESSAGES_STORE], 'readwrite');
        const store = transaction.objectStore(MESSAGES_STORE);
        const request = store.delete(id);
        
        request.onsuccess = () => {
          devLog(`✅ Cleared commit messages for ${tenantId}/${projectId}/${serviceName}`);
          resolve();
        };
        
        request.onerror = () => {
          console.error('Failed to clear commit message:', request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('Failed to clear commit message:', error);
      throw error;
    }
  }

  /**
   * Clear all commit messages for a specific project (all services)
   * @param {string} tenantId - Tenant ID
   * @param {string} projectId - Project ID
   */
  async clearProjectMessages(tenantId, projectId) {
    try {
      await this.init();
      
      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([MESSAGES_STORE], 'readwrite');
        const store = transaction.objectStore(MESSAGES_STORE);
        const index = store.index('projectId');
        const request = index.openCursor(IDBKeyRange.only(projectId));
        
        const itemsToDelete = [];
        
        request.onsuccess = (event) => {
          const cursor = event.target.result;
          if (cursor) {
            const record = cursor.value;
            if (!tenantId || record.tenantId === tenantId) {
              itemsToDelete.push(record.id);
            }
            cursor.continue();
          } else {
            // Delete all collected items
            const deletePromises = itemsToDelete.map(id => {
              return new Promise((deleteResolve, deleteReject) => {
                const deleteRequest = store.delete(id);
                deleteRequest.onsuccess = () => deleteResolve();
                deleteRequest.onerror = () => deleteReject(deleteRequest.error);
              });
            });
            
            Promise.all(deletePromises)
              .then(() => {
                devLog(`✅ Cleared commit messages for project ${projectId} (${itemsToDelete.length} services)`);
                resolve();
              })
              .catch(reject);
          }
        };
        
        request.onerror = () => {
          console.error('Failed to clear project commit messages:', request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('Failed to clear project commit messages:', error);
      throw error;
    }
  }

  /**
   * Get all commit messages grouped by service
   * @param {string} projectId - Project ID
   * @returns {Promise<Array>} - Array of {serviceName, messages, combinedMessage}
   */
  async getAllProjectMessages(projectId) {
    try {
      await this.init();
      
      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([MESSAGES_STORE], 'readonly');
        const store = transaction.objectStore(MESSAGES_STORE);
        const index = store.index('projectId');
        const request = index.openCursor(IDBKeyRange.only(projectId));
        
        const results = [];
        
        request.onsuccess = (event) => {
          const cursor = event.target.result;
          if (cursor) {
            const record = cursor.value;
            results.push({
              serviceName: record.serviceName,
              tenantId: record.tenantId,
              messages: record.messages,
              combinedMessage: record.messages.join('\n'),
              timestamp: record.timestamp
            });
            cursor.continue();
          } else {
            resolve(results);
          }
        };
        
        request.onerror = () => {
          console.error('Failed to get all project commit messages:', request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('Failed to get all project commit messages:', error);
      throw error;
    }
  }

  /**
   * Check if there are pending commit messages for a service
   * @param {string} tenantId - Tenant ID
   * @param {string} projectId - Project ID
   * @param {string} serviceName - Service name
   * @returns {Promise<boolean>}
   */
  async hasMessages(tenantId, projectId, serviceName) {
    try {
      const messages = await this.getMessages(tenantId, projectId, serviceName);
      return messages.length > 0;
    } catch (error) {
      console.error('Failed to check for commit messages:', error);
      return false;
    }
  }
}

// Export singleton instance
const commitMessageStorage = new CommitMessageStorage();
export default commitMessageStorage;
